import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';

import {ReviewsStateType} from '../../../page/main/reviews/reviews-state/reviews-state-type';
import {useLocale} from '../../../provider/locale/locale-hook';
import {useSnackbar} from '../../../provider/snackbar/snackbar-hook';
import {useUser} from '../../../provider/user/user-hook';
import {ExportReportOptionsType, fetchReviewsGetReport} from '../reviews-report';

import {
    createExportTemplate,
    deleteExportTemplate,
    fetchExportConstructorColumns,
    fetchExportConstructorLimits,
    fetchExportConstructorStatus,
    fetchExportTemplate,
    fetchExportTemplates,
    updateExportTemplate,
} from './export-constructor-api';
import {
    CreateExportTemplatePayloadType,
    ExportColumnCatalogItemType,
    ExportConstructorExportPayloadType,
    ExportConstructorLimitsType,
    ExportTemplateType,
    UpdateExportTemplatePayloadType,
} from './export-constructor-type';

const exportTemplatesQueryKey = 'reviews-export-templates';
const exportTemplateQueryKey = 'reviews-export-template';
const exportColumnsQueryKey = 'reviews-export-columns';
const exportLimitsQueryKey = 'reviews-export-limits';

export const defaultExportConstructorLimits: ExportConstructorLimitsType = {
    maxUserTemplates: 50,
};

export function useExportTemplates(enabled = true): UseQueryResult<Array<ExportTemplateType>> {
    const {user} = useUser();

    return useQuery([exportTemplatesQueryKey, user?.id], fetchExportTemplates, {
        enabled: enabled && Boolean(user?.id),
        refetchOnWindowFocus: false,
        retry: false,
    });
}

export function useExportTemplate(templateId: number | null, enabled = true): UseQueryResult<ExportTemplateType> {
    return useQuery([exportTemplateQueryKey, templateId ?? 0], () => fetchExportTemplate(templateId ?? 0), {
        enabled: enabled && Boolean(templateId),
        refetchOnWindowFocus: false,
        retry: false,
    });
}

export function useExportColumns(enabled = true): UseQueryResult<Array<ExportColumnCatalogItemType>> {
    return useQuery([exportColumnsQueryKey], fetchExportConstructorColumns, {
        enabled,
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: Number.POSITIVE_INFINITY,
    });
}

export function useExportLimits(enabled = true): UseQueryResult<ExportConstructorLimitsType> {
    const {user} = useUser();

    return useQuery([exportLimitsQueryKey, user?.id], fetchExportConstructorLimits, {
        enabled: enabled && Boolean(user?.id),
        refetchOnWindowFocus: false,
        retry: false,
    });
}

type ExportMutationVariablesType = {
    payload: ExportConstructorExportPayloadType;
    reviewsState: ReviewsStateType;
    mainFilterKey: string;
};

export function useExportConstructorExportMutation(): UseMutationResult<void, unknown, ExportMutationVariablesType> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();

    return useMutation(
        ({payload, reviewsState, mainFilterKey}) => fetchExportConstructorStatus(payload, reviewsState, mainFilterKey),
        {
            onSuccess: () => {
                snackbar.success({
                    key: 'sent-successfully',
                    message: getLocalizedString(
                        'REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_HAS_BEEN_ACCEPTED__HEADER'
                    ),
                    description: getLocalizedString(
                        'REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_HAS_BEEN_ACCEPTED__TEXT'
                    ),
                });
            },
            onError: () => {
                snackbar.error({
                    message: getLocalizedString('REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_ERROR_SENT'),
                    description: getLocalizedString('SNACKBAR__ERROR__TECH_SUPPORT'),
                });
            },
        }
    );
}

type LegacyExportMutationVariablesType = {
    options: ExportReportOptionsType;
    reviewsState: ReviewsStateType;
    mainFilterKey: string;
};

export function useLegacyReviewsExportMutation(): UseMutationResult<void, unknown, LegacyExportMutationVariablesType> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();

    return useMutation(
        ({options, reviewsState, mainFilterKey}) => fetchReviewsGetReport(options, reviewsState, mainFilterKey),
        {
            onSuccess: () => {
                snackbar.success({
                    key: 'sent-successfully',
                    message: getLocalizedString(
                        'REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_HAS_BEEN_ACCEPTED__HEADER'
                    ),
                    description: getLocalizedString(
                        'REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_HAS_BEEN_ACCEPTED__TEXT'
                    ),
                });
            },
            onError: () => {
                snackbar.error({
                    message: getLocalizedString('REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_ERROR_SENT'),
                    description: getLocalizedString('SNACKBAR__ERROR__TECH_SUPPORT'),
                });
            },
        }
    );
}

export function useCreateExportTemplateMutation(): UseMutationResult<
    ExportTemplateType,
    unknown,
    CreateExportTemplatePayloadType
> {
    const queryClient = useQueryClient();
    const {user} = useUser();
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();

    return useMutation(createExportTemplate, {
        onSuccess: () => {
            queryClient.invalidateQueries([exportTemplatesQueryKey, user?.id]);
            queryClient.invalidateQueries([exportLimitsQueryKey, user?.id]);
            snackbar.success(getLocalizedString('REVIEWS__EXPORT_CONSTRUCTOR__SAVE__SUCCESS'));
        },
        onError: () => {
            snackbar.error(getLocalizedString('REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_ERROR_SENT'));
        },
    });
}

export function useUpdateExportTemplateMutation(): UseMutationResult<
    ExportTemplateType,
    unknown,
    {templateId: number; payload: UpdateExportTemplatePayloadType}
> {
    const queryClient = useQueryClient();
    const {user} = useUser();
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();

    return useMutation(({templateId, payload}) => updateExportTemplate(templateId, payload), {
        onSuccess: (data) => {
            queryClient.invalidateQueries([exportTemplatesQueryKey, user?.id]);
            queryClient.invalidateQueries([exportTemplateQueryKey, data.id]);
            snackbar.success(getLocalizedString('REVIEWS__EXPORT_CONSTRUCTOR__SAVE__SUCCESS'));
        },
        onError: () => {
            snackbar.error(getLocalizedString('REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_ERROR_SENT'));
        },
    });
}

export function useDeleteExportTemplateMutation(): UseMutationResult<void, unknown, number> {
    const queryClient = useQueryClient();
    const {user} = useUser();
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();

    return useMutation(deleteExportTemplate, {
        onSuccess: () => {
            queryClient.invalidateQueries([exportTemplatesQueryKey, user?.id]);
            queryClient.invalidateQueries([exportLimitsQueryKey, user?.id]);
            snackbar.success(getLocalizedString('REVIEWS__EXPORT_CONSTRUCTOR__DELETE__SUCCESS'));
        },
        onError: () => {
            snackbar.error(getLocalizedString('REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_ERROR_SENT'));
        },
    });
}
